import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  TextareaAutosize,
  TextField,
  Typography,
} from '@mui/material';
import { Form, Formik, FormikProps } from 'formik';
import {
  AuthFormValues,
  authInitialValues,
  secondPageSignUpValidationSchema,
} from '../../utils/form-validation.ts';
import {
  creditScores,
  homeStyles,
  incomeRanges,
  states,
  totalBudgets,
} from '../../../../constants/dropdown-options.tsx';
import { AddedCitiesList } from './added-city-list.tsx';
import { AddCitiesButton } from './added-city-button.tsx';

const labelStyle = {
  color: 'white',
  fontSize: '14px',
  marginBottom: '4px',
};

interface SignupBuildInfoProps {
  isPending: boolean;
  createAccount: (values: AuthFormValues) => void;
  postSubmitErrors: string | undefined;
  addedCities: string[];
  setAddedCities: (val: string[]) => void;
}

export const SignupBuildInfo = ({
  isPending,
  createAccount,
  postSubmitErrors,
  addedCities,
  setAddedCities,
}: SignupBuildInfoProps) => {
  return (
    <Box data-testid="signup-form">
      <Typography sx={{ marginBottom: '12px', textAlign: 'center' }}>
        For us to best support you, we need a bit more information. By filling
        out the fields below, our team of experts can best meet your needs.
      </Typography>
      <Formik
        initialValues={authInitialValues}
        validationSchema={secondPageSignUpValidationSchema}
        validateOnBlur={true}
        validateOnMount={true}
        validateOnChange={true}
        onSubmit={(values) => {
          createAccount(values);
        }}
      >
        {({
          values,
          touched,
          errors,
          handleChange,
          setFieldValue,
          isValid,
          submitCount,
        }: FormikProps<AuthFormValues>) => (
          <Form>
            <>
              <Box className="field-group" data-testid="signup-form-page-2">
                <Box className="field">
                  <InputLabel sx={labelStyle}>
                    Build Location - State
                  </InputLabel>
                  <Select
                    className="text-input select"
                    fullWidth
                    id="stateBuildLocation"
                    name="stateBuildLocation"
                    onChange={handleChange}
                    value={values.stateBuildLocation}
                    error={
                      touched.stateBuildLocation &&
                      Boolean(errors.stateBuildLocation)
                    }
                  >
                    {states.map((state: string) => {
                      return (
                        <MenuItem key={state} value={state}>
                          {state}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>Build Location - City</InputLabel>
                  <TextField
                    data-testid="city-field"
                    fullWidth
                    className="text-input"
                    id="citiesBuildLocation"
                    name="citiesBuildLocation"
                    placeholder="Build Location - City"
                    onChange={handleChange}
                    value={values.citiesBuildLocation}
                    error={
                      touched.citiesBuildLocation &&
                      addedCities.length === 0 &&
                      values.citiesBuildLocation === ''
                    }
                  />
                </Box>
              </Box>
              {addedCities.length > 0 && (
                <AddedCitiesList
                  addedCities={addedCities}
                  setAddedCities={setAddedCities}
                />
              )}
              {addedCities.length < 5 && (
                <AddCitiesButton
                  addedCities={addedCities}
                  setAddedCities={setAddedCities}
                  values={values}
                  setFieldValue={setFieldValue}
                />
              )}
              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>Total Budget</InputLabel>
                  <Select
                    className="text-input select"
                    fullWidth
                    id="totalBudget"
                    name="totalBudget"
                    onChange={handleChange}
                    value={values.totalBudget}
                    error={touched.totalBudget && Boolean(errors.totalBudget)}
                  >
                    {totalBudgets.map((totalBudget: string) => {
                      return (
                        <MenuItem key={totalBudget} value={totalBudget}>
                          {totalBudget}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
                <Box>
                  <Typography>Do you need financing?</Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          onChange={(e) => {
                            if (e.currentTarget.value === 'false') {
                              void setFieldValue('needHomeLoan', 'true');
                            } else {
                              void setFieldValue('needHomeLoan', 'false');
                            }
                          }}
                          value={values.needHomeLoan}
                        />
                      }
                      label="Yes"
                    />
                  </FormGroup>
                </Box>
              </Box>
              <Box
                className="field-group"
                sx={{ display: 'flex', flexDirection: 'column' }}
              >
                <Box>
                  <Typography>
                    What are you looking for? (Home, land or both)
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={values.needHome}
                          onChange={(e) => {
                            if (e.currentTarget.value === 'false') {
                              void setFieldValue('needHome', 'true');
                            } else {
                              void setFieldValue('needHome', 'false');
                            }
                          }}
                        />
                      }
                      label="Home"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          value={values.needLand}
                          onChange={(e) => {
                            if (e.currentTarget.value === 'false') {
                              void setFieldValue('needLand', 'true');
                            } else {
                              void setFieldValue('needLand', 'false');
                            }
                          }}
                        />
                      }
                      label="Land"
                    />
                  </FormGroup>
                </Box>
              </Box>
              <Box>
                <Box
                  className="field-group"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginBottom: '12px',
                  }}
                >
                  <Box>
                    <Typography>
                      (Optional) Is this a secondary residence?
                    </Typography>
                    <FormGroup>
                      <FormControlLabel
                        control={
                          <Checkbox
                            onChange={(e) => {
                              if (e.currentTarget.value === 'false') {
                                void setFieldValue(
                                  'isSecondaryResidence',
                                  'true'
                                );
                              } else {
                                void setFieldValue(
                                  'isSecondaryResidence',
                                  'false'
                                );
                              }
                            }}
                            value={values.isSecondaryResidence}
                          />
                        }
                        label="Yes"
                      />
                    </FormGroup>
                  </Box>
                </Box>
              </Box>

              <Box className="field-group">
                <Box className="field">
                  <InputLabel sx={labelStyle}>
                    (Optional) Household Income
                  </InputLabel>
                  <Select
                    className="text-input select"
                    fullWidth
                    id="annualGrossIncome"
                    name="annualGrossIncome"
                    label="Optional - Household Income"
                    onChange={handleChange}
                    value={values.annualGrossIncome}
                    error={
                      touched.annualGrossIncome &&
                      Boolean(errors.annualGrossIncome)
                    }
                  >
                    {incomeRanges.map((incomeRange: string) => {
                      return (
                        <MenuItem key={incomeRange} value={incomeRange}>
                          {incomeRange}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
                <Box className="field">
                  <InputLabel sx={labelStyle}>
                    (Optional) Credit Score
                  </InputLabel>
                  <Select
                    className="text-input select"
                    fullWidth
                    id="creditScore"
                    name="creditScore"
                    onChange={handleChange}
                    value={values.creditScore}
                    error={touched.creditScore && Boolean(errors.creditScore)}
                  >
                    {creditScores.map((creditScore: string) => {
                      return (
                        <MenuItem key={creditScore} value={creditScore}>
                          {creditScore}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </Box>
              </Box>
              <Box className="field">
                <InputLabel sx={labelStyle}>(Optional) Home Style</InputLabel>
                <Select
                  fullWidth
                  className="text-input select homeStylePreference"
                  id="homeStylePreference"
                  name="homeStylePreference"
                  defaultValue="Optional - Home Style"
                  onChange={handleChange}
                  value={values.homeStylePreference}
                  error={
                    touched.homeStylePreference &&
                    Boolean(errors.homeStylePreference)
                  }
                >
                  {homeStyles.map((homeStyle: string) => {
                    return (
                      <MenuItem key={homeStyle} value={homeStyle}>
                        {homeStyle}
                      </MenuItem>
                    );
                  })}
                </Select>
                {values.homeStylePreference === 'Other' && (
                  <TextareaAutosize
                    className="text-box"
                    id="homeStylePreferenceOtherDescription"
                    name="homeStylePreferenceOtherDescription"
                    placeholder="Home Style Description"
                    onChange={handleChange}
                    value={values.homeStylePreferenceOtherDescription}
                  />
                )}
              </Box>
              <TextareaAutosize
                className="text-box"
                id="freeFormAbout"
                name="freeFormAbout"
                placeholder="(Optional) What's important to you?"
                onChange={handleChange}
                value={values.freeFormAbout}
              />
              {submitCount > 1 && Object.values(errors).length > 0 && (
                <Box className="error-text" data-testid="errors">
                  <Typography sx={{ borderBottom: 'solid 1px white' }}>
                    Missing Required Fields:{' '}
                  </Typography>
                  <ul>
                    {Object.values(errors).map((error) => {
                      return <li key={error}>{error}</li>;
                    })}
                  </ul>
                </Box>
              )}
              {postSubmitErrors && (
                <Box className="error-text">
                  <Typography sx={{ borderBottom: 'solid 1px white' }}>
                    Something's wrong:{' '}
                  </Typography>
                  <ul>
                    <li>{postSubmitErrors}</li>
                  </ul>
                </Box>
              )}
              <Button
                data-testid="submit-button"
                disabled={isPending || !isValid}
                type={'submit'}
                sx={{
                  minWidth: '100%',
                  marginTop: '12px',
                }}
              >
                Create Account
              </Button>
            </>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
